/**
 * Presentation formatters.
 *
 * Kept in one module, and kept pure, because every one of them has a bug that
 * only appears on data the author did not think of: a due date that is today, a
 * timestamp in the future because two machines disagree about the clock, a file
 * size of zero. Having them in one file means those cases are visible next to
 * each other rather than spread across eight components.
 */

import type { Priority } from '@slate/shared';

/** Relative time, for the activity feed and comment headers.
 *
 * Deliberately coarse above an hour and absolute beyond a week. "43 minutes ago"
 * is more useful than a timestamp when you are reading a live feed; "3 days ago"
 * is less useful than a date when you are reading history. `Intl.RelativeTimeFormat`
 * is used for the phrasing so the wording is not hard-coded English. */
const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto', style: 'short' });

export function relativeTime(iso: string, now: number = Date.now()): string {
  const then = Date.parse(iso);
  // A parse failure or a clock skew that puts the timestamp in the future both
  // land here. Returning the raw string is honest; rendering "in -3 seconds"
  // would not be.
  if (Number.isNaN(then)) return iso;

  const seconds = Math.round((then - now) / 1000);
  const absolute = Math.abs(seconds);

  if (absolute < 45) return 'just now';
  if (absolute < 90) return RELATIVE.format(Math.round(seconds / 60), 'minute');
  if (absolute < 3600) return RELATIVE.format(Math.round(seconds / 60), 'minute');
  if (absolute < 86_400) return RELATIVE.format(Math.round(seconds / 3600), 'hour');
  if (absolute < 604_800) return RELATIVE.format(Math.round(seconds / 86_400), 'day');

  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(then);
}

export function absoluteTime(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return iso;
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(then);
}

/**
 * A due date is a calendar date, not an instant - see `DateOnly` in
 * `packages/shared/src/domain.ts`.
 *
 * `new Date('2026-03-14')` parses as UTC midnight, and formatting it in a
 * timezone behind UTC renders the 13th. That is the single most common bug in
 * due-date code, so the string is split and the parts are fed to `Date` in local
 * time instead of being parsed.
 */
export function formatDueDate(dateOnly: string): string {
  const parts = dateOnly.split('-');
  if (parts.length !== 3) return dateOnly;
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return dateOnly;

  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(
    new Date(year, month - 1, day),
  );
}

/** Whole days from today to a due date. Negative means overdue. `null` when the
 * date is unparseable, which the caller renders as no badge rather than as
 * "overdue". */
export function daysUntilDue(dateOnly: string, now: number = Date.now()): number | null {
  const parts = dateOnly.split('-');
  if (parts.length !== 3) return null;
  const target = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  if (Number.isNaN(target.getTime())) return null;

  const today = new Date(now);
  const midnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((target.getTime() - midnight.getTime()) / 86_400_000);
}

/** Whether a due date should be shown as overdue. Today is not overdue - a card
 * due today is the one you are supposed to be working on. */
export function isOverdue(dateOnly: string, now: number = Date.now()): boolean {
  const days = daysUntilDue(dateOnly, now);
  return days !== null && days < 0;
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Up to two initials, for the avatar fallback. Uses code-point iteration rather
 * than `charAt` so a name beginning with an emoji or a non-BMP character does
 * not render as half a surrogate pair. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter((word) => word.length > 0);
  if (words.length === 0) return '?';
  const first = Array.from(words[0] ?? '')[0] ?? '?';
  if (words.length === 1) return first.toUpperCase();
  const second = Array.from(words[words.length - 1] ?? '')[0] ?? '';
  return (first + second).toUpperCase();
}

export const PRIORITY_LABEL: Record<Priority, string> = {
  none: 'No priority',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

/** Tailwind cannot build a class name from a runtime string, so the mapping from
 * priority to utility is written out. A template literal like
 * `text-priority-${priority}` produces a class that Tailwind never generates,
 * which fails silently as an unstyled element. */
export const PRIORITY_CLASS: Record<Priority, string> = {
  none: 'text-priority-none',
  low: 'text-priority-low',
  medium: 'text-priority-medium',
  high: 'text-priority-high',
  urgent: 'text-priority-urgent',
};

export const PRIORITY_BG_CLASS: Record<Priority, string> = {
  none: 'bg-priority-none',
  low: 'bg-priority-low',
  medium: 'bg-priority-medium',
  high: 'bg-priority-high',
  urgent: 'bg-priority-urgent',
};

/** Pick black or white text for an arbitrary hex background, so a user-chosen
 * label colour is always readable. Uses the WCAG relative-luminance formula
 * rather than a naive average of the channels, which gets saturated colours
 * wrong. */
export function readableInkOn(hex: string): string {
  const normalised = hex.replace('#', '');
  if (normalised.length !== 6) return '#ffffff';
  const r = Number.parseInt(normalised.slice(0, 2), 16) / 255;
  const g = Number.parseInt(normalised.slice(2, 4), 16) / 255;
  const b = Number.parseInt(normalised.slice(4, 6), 16) / 255;
  const channel = (value: number) => (value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
  return luminance > 0.45 ? '#12151c' : '#ffffff';
}
